import React, { useCallback, useId, useMemo, useState } from 'react';
import { GradientToolkitIcon, InputSearch, confirmDialog, globalToastService } from '@repo/atomic-components';
import { ToolkitGroup, ToolkitGroupCollection, ToolkitTool, useDeleteUserToolkitToolMutation } from '@/api/toolkit';
import { CollectionMatchCounts, selectUserAboutData, useAppSelector } from '@/store';
import { ToolItem } from './ToolItem';
import { ToolkitLoader } from './ToolkitLoader';
import { ToolkitEmptyState } from './ToolkitEmptyState';
import { ToolkitTabs } from './ToolkitTabs';
import { MyPromptsSection } from './MyPromptsSection';
import { CreateUserToolkitPromptModal } from './CreateUserToolkitPromptModal';
import { EditUserToolkitPromptModal } from './EditUserToolkitPromptModal';
import { RequestGlobalToolkitModal } from './RequestGlobalToolkitModal';
import { ToolkitPromptPreviewModal } from './ToolkitPromptPreviewModal';
import { DeleteConfirmationDialog } from '@/ui/components/DeleteConfirmationDialog';
import { DELETE_USER_TOOLKIT_PROMPT_CONFIRM } from '@/constants';
import { normalizeToolkitSearchQuery, toolkitToolMatchesSearch } from '@/utils/toolkitSearch';
import { UserRoleType } from '@/api';

interface ToolkitModalContentProps {
  searchQuery: string;
  isLoading: boolean;
  filteredGroups: ToolkitGroup[];
  collections: ToolkitGroupCollection[];
  activeCollectionTitle: string | null;
  collectionMatchCounts: CollectionMatchCounts;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleToolClick: (tool: ToolkitTool) => void;
  handleCollectionChange: (collectionTitle: string) => void;
  // When true, use a tighter responsive grid tuned for the in-chat panel.
  compact?: boolean;
}

export const ToolkitModalContent = ({
  searchQuery,
  isLoading,
  filteredGroups,
  collections,
  activeCollectionTitle,
  collectionMatchCounts,
  handleSearchChange,
  handleToolClick,
  handleCollectionChange,
  compact = false,
}: ToolkitModalContentProps) => {
  const userTools = useAppSelector((state) => state.toolkit.userTools);
  const userAboutData = useAppSelector(selectUserAboutData);
  const globalAdminAsUser = userAboutData?.role === UserRoleType.GLOBAL_ADMIN ? userAboutData.userId : undefined;
  const searchInputId = useId();
  const [isCreatePromptOpen, setIsCreatePromptOpen] = useState(false);
  const [promptToEdit, setPromptToEdit] = useState<ToolkitTool | null>(null);
  const [promptForGlobalToolkit, setPromptForGlobalToolkit] = useState<ToolkitTool | null>(null);
  const [promptToPreview, setPromptToPreview] = useState<ToolkitTool | null>(null);
  const [deleteUserToolkitTool] = useDeleteUserToolkitToolMutation();

  const handleDeletePrompt = useCallback(
    async (tool: ToolkitTool) => {
      const confirmed = await confirmDialog(
        {
          id: DELETE_USER_TOOLKIT_PROMPT_CONFIRM,
          toolId: tool.toolId,
        },
        { forceClosePrev: true },
      );

      if (!confirmed) return;

      try {
        await deleteUserToolkitTool({
          toolId: tool.toolId,
          ...(globalAdminAsUser ? { globalAdminAsUser } : {}),
        }).unwrap();
        globalToastService.show({
          closable: true,
          severity: 'success',
          summary: 'Your prompt has been deleted',
          life: 6000,
        });
      } catch {
        globalToastService.show({
          closable: true,
          severity: 'error',
          summary: 'Failed to delete, please try again.',
          life: 6000,
        });
      }
    },
    [deleteUserToolkitTool, globalAdminAsUser],
  );

  const gridClassName = compact ? 'toolkit-grid--compact gap-6' : 'toolkit-grid gap-6';

  const filteredMyPrompts = useMemo(() => {
    const normalizedQuery = normalizeToolkitSearchQuery(searchQuery);
    if (!normalizedQuery) {
      return userTools;
    }
    return userTools.filter((tool) => toolkitToolMatchesSearch(tool, normalizedQuery));
  }, [searchQuery, userTools]);

  const hasSearchQuery = !!normalizeToolkitSearchQuery(searchQuery);
  // A search hides the section when nothing in it matches, like the collection groups.
  const showMyPrompts = !isLoading && (!hasSearchQuery || filteredMyPrompts.length > 0);

  const totalCollectionMatchCount = useMemo(
    () => Object.values(collectionMatchCounts).reduce((sum, count) => sum + count, 0),
    [collectionMatchCounts],
  );

  const isPreviewedToolUserOwned = !!promptToPreview && userTools.some((t) => t.toolId === promptToPreview.toolId);

  const showToolkitEmptyState =
    !isLoading &&
    filteredGroups.length === 0 &&
    (!hasSearchQuery || (filteredMyPrompts.length === 0 && totalCollectionMatchCount === 0));

  const rootClassName = compact ? 'flex w-full flex-col' : 'flex h-full min-h-0 flex-1 flex-col overflow-hidden';

  const scrollRegionClassName = compact
    ? '@container flex w-full flex-col gap-0 pb-4 pr-2'
    : '@container flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto pb-4 pr-2';

  return (
    <div className={rootClassName}>
      {/* Modal: scroll here. Panel: parent ToolkitPanel scrolls; search is sticky to that scroll. */}
      <div className={scrollRegionClassName}>
        <div className="bg-theme-white sticky top-0 z-20 shrink-0 pb-6">
          <div className="w-full">
            {/* A placeholder is not an accessible name (WCAG 3.3.2); the modal and the side
                panel both render this, so the id is per-instance. */}
            <label htmlFor={searchInputId} className="sr-only">
              Search prompts
            </label>
            <InputSearch
              id={searchInputId}
              type="text"
              placeholder="What are you looking for?"
              className="!bg-theme-white !text-theme-black !border-theme-border-grey !placeholder-theme-text-grey [&_button:hover]:!text-fletchers-gray-200 h-8 w-full [&_input]:text-sm [&_input]:placeholder:text-sm"
              value={searchQuery}
              onChange={handleSearchChange}
              isLoading={isLoading}
              clearIconClassName="text-theme-black"
            />
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {showMyPrompts && (
            <MyPromptsSection
              prompts={filteredMyPrompts}
              compact={compact}
              handleToolClick={handleToolClick}
              onCreatePrompt={() => setIsCreatePromptOpen(true)}
              onEditPrompt={setPromptToEdit}
              onRequestGlobalToolkit={setPromptForGlobalToolkit}
              onDeletePrompt={handleDeletePrompt}
              onExpandPrompt={setPromptToPreview}
            />
          )}

          {!isLoading && (
            <ToolkitTabs
              collections={collections}
              activeCollectionTitle={activeCollectionTitle}
              collectionMatchCounts={collectionMatchCounts}
              onCollectionChange={handleCollectionChange}
            />
          )}

          {isLoading && <ToolkitLoader />}
          {showToolkitEmptyState && <ToolkitEmptyState hasSearchQuery={hasSearchQuery} />}
          {!isLoading && filteredGroups.length > 0 && (
            <div className="flex flex-col gap-6">
              {filteredGroups.map((group: ToolkitGroup, groupIndex: number) => (
                <div key={groupIndex} className="flex flex-col gap-6">
                  <div className="flex items-center gap-2">
                    <GradientToolkitIcon />
                    <h3 className="text-theme-black text-lg font-semibold">{group.groupTitle}</h3>
                  </div>

                  <div className={gridClassName}>
                    {group.tools.map((tool) => (
                      <ToolItem
                        key={tool.toolId}
                        tool={tool}
                        handleToolClick={handleToolClick}
                        onExpand={setPromptToPreview}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateUserToolkitPromptModal visible={isCreatePromptOpen} onHide={() => setIsCreatePromptOpen(false)} />
      <EditUserToolkitPromptModal visible={!!promptToEdit} onHide={() => setPromptToEdit(null)} tool={promptToEdit} />
      <RequestGlobalToolkitModal
        visible={!!promptForGlobalToolkit}
        onHide={() => setPromptForGlobalToolkit(null)}
        tool={promptForGlobalToolkit}
      />
      <ToolkitPromptPreviewModal
        visible={!!promptToPreview}
        tool={promptToPreview}
        onHide={() => setPromptToPreview(null)}
        onSendToChat={(tool) => {
          setPromptToPreview(null);
          handleToolClick(tool);
        }}
        onEdit={
          isPreviewedToolUserOwned
            ? (tool) => {
                setPromptToPreview(null);
                setPromptToEdit(tool);
              }
            : undefined
        }
        onRequestGlobalToolkit={
          isPreviewedToolUserOwned
            ? (tool) => {
                setPromptToPreview(null);
                setPromptForGlobalToolkit(tool);
              }
            : undefined
        }
      />
      <DeleteConfirmationDialog
        id={DELETE_USER_TOOLKIT_PROMPT_CONFIRM}
        title="Are you sure you would like to delete this prompt?"
        description="This action cannot be undone."
        style={{ width: '19.6875rem', height: '18.0625rem' }}
      />
    </div>
  );
};
